import Brdf from './Brdf';
import Coloring from './Coloring';
import CurrentLayer from './CurrentLayer';
import Output from './Output';
import Rectangle from './Rectangle';
import stats from './Stats';
import Surface from './Surface';
import TextureMixer from './TextureMixer';
import calculateSettings from './calculations/calculateSettings';
import DoubleBrush from './doubleLayer/DoubleBrush';
import DoubleBrushViewer from './doubleLayer/DoubleBrushViewer';
import DoubleSplat from './doubleLayer/DoubleSplat';
import DoubleSplatPencil from './doubleLayer/DoubleSplatPencil';
import SingleBrush from './singleLayer/SingleBrush';
import SingleBrushViewer from './singleLayer/SingleBrushViewer';
import SingleSplat from './singleLayer/SingleSplat';
import SingleSplatPencil from './singleLayer/SingleSplatPencil';
import TripleBrush from './tripleLayer/TripleBrush';
import TripleBrushViewer from './tripleLayer/TripleBrushViewer';
import TripleSplat from './tripleLayer/TripleSplat';
import TripleSplatPencil from './tripleLayer/TripleSplatPencil';

const IS_DEV = true;

const layerTypes = ['ERROR', 'singleLayer', 'doubleLayer', 'tripleLayer'];

class Painting {
  painting = false;

  needsRedraw = true;

  viewportUpdated = true;

  blocked = false;

  printCurrentLayer = false;

  testingState = {
    // testedTexture: 'brushPositionsTexture_x',
  };

  layerType = layerTypes[1];

  constructor(gl, shaders, viewport, textureManager, data) {
    this.gl = gl;
    this.shaders = shaders;
    this.viewport = viewport;
    this.textureManager = textureManager;
    this.data = data;

    this.output = new Output(this.gl, this.shaders, this.viewport);

    this.stopPainting = this.stopPainting.bind(this);

    // triple layer
    this.tripleLayer = {};
    this.tripleLayer.brush = new TripleBrush(this.gl, this.shaders, this.textureManager);
    this.tripleLayer.brushViewer = new TripleBrushViewer(this.gl, this.shaders, this.viewport, this.textureManager, this.tripleLayer.brush);
    this.tripleLayer.splatBrush = new TripleSplat(this.gl, this.shaders, this.textureManager, this.tripleLayer.brush, this.stopPainting);
    this.tripleLayer.splatPencil = new TripleSplatPencil(this.gl, this.shaders, this.textureManager, this.tripleLayer.brush, this.stopPainting);

    // single layer
    this.singleLayer = {};
    this.singleLayer.brush = new SingleBrush(this.gl, this.shaders, this.textureManager);
    this.singleLayer.brushViewer = new SingleBrushViewer(this.gl, this.shaders, this.viewport, this.textureManager, this.singleLayer.brush);
    this.singleLayer.splatBrush = new SingleSplat(this.gl, this.shaders, this.textureManager, this.singleLayer.brush, this.stopPainting);
    this.singleLayer.splatPencil = new SingleSplatPencil(this.gl, this.shaders, this.textureManager, this.singleLayer.brush, this.stopPainting);

    // double layer
    this.doubleLayer = {};
    this.doubleLayer.brush = new DoubleBrush(this.gl, this.shaders, this.textureManager);
    this.doubleLayer.brushViewer = new DoubleBrushViewer(this.gl, this.shaders, this.viewport, this.textureManager, this.doubleLayer.brush);
    this.doubleLayer.splatBrush = new DoubleSplat(this.gl, this.shaders, this.textureManager, this.doubleLayer.brush, this.stopPainting);
    this.doubleLayer.splatPencil = new DoubleSplatPencil(this.gl, this.shaders, this.textureManager, this.doubleLayer.brush, this.stopPainting);

    this.coloring = new Coloring(this.gl, this.shaders, this.textureManager, this.data);
    this.currentLayer = new CurrentLayer(this.gl, this.shaders, this.textureManager, this.data);
    this.brdf = new Brdf(this.gl, this.shaders, this.textureManager);
    this.surface = new Surface(this.gl, this.shaders, this.textureManager);

    this.textureMixer = new TextureMixer(this.gl, this.shaders, this.textureManager);

    this.initializeTool();

    this.loop = this.loop.bind(this);
    this.shouldComponentUpdate = this.shouldComponentUpdate.bind(this);

    this.loop();
  }

  loop() {
    stats.begin();

    if (this.initialized && !this.blocked) {
      this.frame();
    }

    stats.end();

    window.requestAnimationFrame(this.loop);
  }

  stopPainting() {
    this.painting = false;
  }

  frame() {
    try {
      if (this.painting) {
        this[this.layerType].brush.setPosition(this.currentX, this.currentY, this.currentPressure);

        const brushArea = this[this.layerType].splatBrush.splat(this.currentPressure);
        this[this.layerType].splatPencil.splat(this.currentPressure, brushArea);
      }

      let area = this[this.layerType].splatBrush.simulate();
      // this[this.layerType].splatPencil.simulate();

      if (area) {
        this.needsRedraw = true;
      }

      if (this.needsRedraw) {
        if (area === undefined || this.viewportUpdated) {
          area = new Rectangle(0, 0, this.textureManager.width, this.textureManager.height);
        }

        // this.coloring.updateReflectances(area);
        // this.coloring.calculateColorFromReflectance(area, true);

        this.coloring.calculateColor(area);

        this.surface.connect(area);
        this.brdf.render(area);

        if (this.printCurrentLayer) {
          this.currentLayer.calculateColor();
          this.currentLayer.connect();
          this.currentLayer.render();
        }
      }

      if (this.needsRedraw || this.viewportUpdated) {
        if (this.testingState.testedTexture) {
          this.output.print(this.textureManager[this.testingState.testedTexture]);
        } else {
          this.output.print(this.textureManager.brdfTexture);
        }

        if (this.painting && this.state?.properties.ui.showBrush) {
          this[this.layerType].brushViewer.print();
        }

        this.viewportUpdated = false;
        this.needsRedraw = false;
      }
    } catch (e) {
      console.log(e);
    }
  }

  initializeTool() {
    this.start = this.start.bind(this);
    this.move = this.move.bind(this);
    this.stop = this.stop.bind(this);
    this.changeViewport = this.changeViewport.bind(this);

    this.viewport.setHandlers(this.start, this.move, this.stop, this.changeViewport);
  }

  start(coords, pressure) {
    this.setCurrentPointerValues(coords, pressure);

    this[this.layerType].brush.initialize(coords[0], coords[1]);

    this.painting = true;
  }

  move(coords, pressure) {
    this.setCurrentPointerValues(coords, pressure);
  }

  stop(/* coords, pressure */) {
    this.painting = false;
  }

  setCurrentPointerValues(coords, pressure) {
    this.currentX = coords[0];
    this.currentY = coords[1];

    if (this.settings.getForceFromPen) {
      this.currentPressure = pressure;
    } else {
      this.currentPressure = this.settings.force;
    }
  }

  changeViewport() {
    this.viewportUpdated = true;
  }

  shouldComponentUpdate(state) {
    if (
      this.state?.controlling !== state.controlling ||
      this.state?.painting !== state.painting ||
      this.state?.properties !== state.properties ||
      this.state?.configuration !== state.configuration ||
      this.state?.testing !== state.testing ||
      (IS_DEV && this.state.presets !== state.presets)
    ) {
      return true;
    }

    return false;
  }

  update(state) {
    this.viewport.update(state);

    if (this.shouldComponentUpdate(state)) {
      const settings = calculateSettings(state);

      this.settings = settings;

      this.blocked = state.controlling.blocked;

      if (this.blocked) {
        this.painting = false;
      }

      this.layerType = layerTypes[state.controlling.layersType];

      this[this.layerType].brush.update(settings);
      this[this.layerType].splatBrush.update(settings);
      this[this.layerType].splatPencil.update(settings);

      this.brdf.update(settings);
      this.surface.update(settings);
      this.coloring.update(settings);
      this.currentLayer.update(settings, state.properties.currentLayer);

      this.setTestingState(state);
    }

    this.state = state;

    this.initialized = true;

    this.needsRedraw = true;
  }

  clear() {
    this.needsRedraw = true;
    this.viewportUpdated = true;
  }

  dry() {
    this.textureMixer.intersection('splatPencilPaintTexture', 'splatBrushPaintTexture', 'splatPencilHelperTexture');
    this.textureManager.swap('splatPencilPaintTexture', 'splatPencilHelperTexture');

    // render tmp
    const area = new Rectangle(0, 0, this.textureManager.width, this.textureManager.height);

    this.coloring.updateReflectances(area);

    // this.coloring.calculateColorFromReflectance(area, true);
    this.surface.connect(area);

    this.textureManager.swap('splatPencilPaintTexture', 'splatPencilHelperTexture');

    this.textureMixer.difference('splatPencilPaintTexture', 'splatBrushPaintTexture', 'splatPencilHelperTexture');
    this.textureManager.swap('splatPencilPaintTexture', 'splatPencilHelperTexture');
  }

  setTestingState(state) {
    this.testingState = state.testing;
  }

  startPrintCurrentLayer() {
    this.printCurrentLayer = true;
    this.needsRedraw = true;
  }

  stopPrintCurrentLayer() {
    this.printCurrentLayer = false;
  }
}

export default Painting;
